/**
 * Pemindahan bahan baku antar store — K197 butir 5 (BUSINESS-29, vonis Owner 25 Sep 2026):
 * "PERLU, dua langkah (kirim → terima), diajukan Admin Gudang, disetujui Kepala Cabang, lot ikut pindah. Nilai bahan
 *  pindah dengan harga rata-rata region ASAL dan masuk ke rata-rata region TUJUAN."
 * Tombol mengikuti `aksi` yang dikirim backend per baris — layar tidak menebak hak dari nama peran.
 *
 * Pola daftar seragam (28 Sep 2026): daftar = mode SERVER (dulu SELURUH pemindahan dikirim tanpa batas),
 * saringan bawaan "Belum selesai" (chip bisa dihapus), pilihan bahan lewat `PemilihCari`.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { apiErrorMessage } from "../../lib/inventoryApi";
import { ambilSemua } from "../../lib/ambilSemua";
import PemilihCari from "../../components/etams/PemilihCari";
import { cariBahan, labelBahan } from "../../lib/sumberPilihan";
import { useDaftar } from "../../hooks/useDaftar";
import { PalangDaftar, ThUrut, Paginasi } from "../../components/etams/PalangDaftar";
import { useAuth } from "../../contexts/AuthContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { StatusBadge } from "../../components/etams/StatusBadge";
import { formatDate, formatIDR } from "../../lib/format";
import Hint from "../../components/etams/Hint";

const NADA = { DIAJUKAN: "warning", DISETUJUI: "info", DIKIRIM: "info", DITERIMA: "success", DITOLAK: "danger", DIBATALKAN: "neutral" };
const LABEL = { DIAJUKAN: "Diajukan", DISETUJUI: "Disetujui", DIKIRIM: "Dikirim", DITERIMA: "Diterima", DITOLAK: "Ditolak", DIBATALKAN: "Dibatalkan" };
const barisKosong = () => ({ material_id: "", bahan: null, qty: "", lot_asal_id: "" });
const LABEL_SARING = { BELUM_SELESAI: "Belum selesai", ...LABEL };
//: Cermin `URUT_SAH_PINDAH` di backend/services/pindah_bahan.py.
const URUT_SAH = ["diajukan_pada", "nomor", "status"];
const KOLOM_CSV = [
  { kunci: "nomor", judul: "Nomor" },
  { kunci: "store_asal_nama", judul: "Asal" }, { kunci: "store_tujuan_nama", judul: "Tujuan" },
  { kunci: "diajukan_pada", judul: "Diajukan", ambil: (p) => formatDate(p.diajukan_pada) },
  { kunci: "status", judul: "Status", ambil: (p) => LABEL[p.status] || p.status },
  { kunci: "alasan", judul: "Alasan" },
];

export default function PindahBahan() {
  const { roleCode } = useAuth();
  const [baris, setBaris] = useState([]);
  const [total, setTotal] = useState(0);
  const [memuat, setMemuat] = useState(true);
  const [detail, setDetail] = useState(null);
  const [stores, setStores] = useState([]);
  const [tujuanOpsi, setTujuanOpsi] = useState([]);
  const [buat, setBuat] = useState(false);
  const [form, setForm] = useState({ store_asal_id: "", store_tujuan_id: "", alasan: "", items: [barisKosong()] });
  const [lot, setLot] = useState({});
  const [alasan, setAlasan] = useState("");
  const [busy, setBusy] = useState(false);

  const daftar = useDaftar(baris, {
    server: { total, urutSah: URUT_SAH },
    urutAwal: { kunci: "diajukan_pada", arah: "desc" },
    saringan: { status: { label: "Status", param: "status", bawaan: "BELUM_SELESAI", tampil: (v) => LABEL_SARING[v] || v } },
  });
  const { cari, limit, offset, sort, arah } = daftar.paramServer;
  const { paramSaringan } = daftar;
  const bangunParams = useCallback((o = {}) => ({
    ...paramSaringan, ...(cari && { q: cari }), limit, offset, sort, arah, ...o,
  }), [paramSaringan, cari, limit, offset, sort, arah]);
  const ambilHalaman = useCallback(async (off, n) =>
    (await api.get("/inventory/pindah-bahan", { params: bangunParams({ offset: off, limit: n }) })).data, [bangunParams]);
  const muat = useCallback(async () => {
    setMemuat(true);
    try {
      const r = await api.get("/inventory/pindah-bahan", { params: bangunParams() });
      setBaris(r.data.items || []); setTotal(r.data.total ?? 0);
    } catch (e) { toast.error(apiErrorMessage(e, "Daftar pemindahan gagal dimuat.")); }
    finally { setMemuat(false); }
  }, [bangunParams]);
  useEffect(() => { muat(); }, [muat]);
  useEffect(() => {
    api.get("/stores").then(r => setStores(r.data || [])).catch((e) => { setStores([]); toast.error(apiErrorMessage(e, "Daftar cabang gagal dimuat.")); });
    api.get("/stores", { params: { untuk: "tujuan-transfer" } }).then(r => setTujuanOpsi(r.data || [])).catch((e) => { setTujuanOpsi([]); toast.error(apiErrorMessage(e, "Daftar cabang tujuan gagal dimuat.")); });
  }, []);
  // Lot per bahan di store asal (lot ikut pindah).
  useEffect(() => {
    const asal = form.store_asal_id;
    const ids = [...new Set(form.items.map(i => i.material_id).filter(Boolean))];
    ids.forEach(mid => {
      const k = `${asal}:${mid}`;
      if (!asal || lot[k]) return;
      ambilSemua((p) => api.get("/inventory/fabric-lots", { params: p }), { material_id: mid, store_id: asal })
        .then(l => setLot(x => ({ ...x, [k]: l }))).catch((e) => { setLot(x => ({ ...x, [k]: [] })); toast.error(apiErrorMessage(e, "Daftar lot gagal dimuat.")); });
    });
  }, [form.store_asal_id, form.items, lot]);

  const bukaDetail = async (id) => {
    try { const r = await api.get(`/inventory/pindah-bahan/${id}`); setDetail(r.data); setAlasan(""); }
    catch (e) { toast.error(apiErrorMessage(e)); }
  };
  const aksi = async (jalur, badan, pesan) => {
    setBusy(true);
    try {
      const r = await api.post(`/inventory/pindah-bahan/${detail.id}/${jalur}`, badan || {});
      toast.success(pesan); setDetail(r.data); muat();
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setBusy(false); }
  };
  const kirimPengajuan = async () => {
    setBusy(true);
    try {
      const items = form.items.filter(i => i.material_id && Number(i.qty) > 0)
        .map(i => ({ material_id: i.material_id, qty: Number(i.qty), lot_asal_id: i.lot_asal_id || null }));
      await api.post("/inventory/pindah-bahan", { ...form, items });
      toast.success("Pemindahan diajukan — menunggu persetujuan Kepala Cabang.");
      setBuat(false); setForm({ store_asal_id: "", store_tujuan_id: "", alasan: "", items: [barisKosong()] }); muat();
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setBusy(false); }
  };
  const ubahBaris = (i, patch) => setForm(f => ({ ...f, items: f.items.map((b, j) => (j === i ? { ...b, ...patch } : b)) }));

  return (
    <div className="max-w-5xl space-y-4" data-testid="pindah-bahan-page">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold">
            Pindah Bahan Baku{" "}
            <Hint>Lot ikut pindah; nilainya memakai harga rata-rata cabang asal.</Hint>
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Antar store: diajukan Admin Gudang, disetujui Kepala Cabang, lalu dikirim dan diterima.
          </p>
        </div>
        {roleCode === "ADMIN_GUDANG" && <Button size="sm" onClick={() => setBuat(!buat)} data-testid="pb-buat">Ajukan pemindahan</Button>}
      </div>

      {buat && (
        <Card className="space-y-3 p-4 text-sm" data-testid="pb-form">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <label>Store asal
              <select className="mt-1 h-9 w-full rounded border bg-background px-2" value={form.store_asal_id}
                      onChange={e => setForm({ ...form, store_asal_id: e.target.value })} data-testid="pb-asal">
                <option value="">— pilih —</option>
                {stores.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select></label>
            <label>Store tujuan
              <select className="mt-1 h-9 w-full rounded border bg-background px-2" value={form.store_tujuan_id}
                      onChange={e => setForm({ ...form, store_tujuan_id: e.target.value })} data-testid="pb-tujuan">
                <option value="">— pilih —</option>
                {tujuanOpsi.filter(s => s.id !== form.store_asal_id).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select></label>
          </div>
          {form.items.map((b, i) => {
            const lots = lot[`${form.store_asal_id}:${b.material_id}`] || [];
            return (
              <div key={i} className="grid grid-cols-1 gap-2 sm:grid-cols-4">
                <PemilihCari value={b.bahan} className="sm:col-span-2" placeholder="— bahan —"
                             onChange={m => ubahBaris(i, { bahan: m, material_id: m?.id || "", lot_asal_id: "" })}
                             cari={cariBahan} label={(m) => `${labelBahan(m)}${m?.unit_of_measure ? ` (${m.unit_of_measure})` : ""}`}
                             testId={`pb-bahan-${i}`} />
                <Input type="number" step="0.001" placeholder="Qty" value={b.qty} onChange={e => ubahBaris(i, { qty: e.target.value })}
                       data-testid={`pb-qty-${i}`} />
                <select className="h-9 rounded border bg-background px-2" value={b.lot_asal_id}
                        onChange={e => ubahBaris(i, { lot_asal_id: e.target.value })} disabled={!lots.length}>
                  <option value="">{lots.length ? "— lot (opsional) —" : "tanpa lot"}</option>
                  {lots.map(l => <option key={l.id} value={l.id}>{l.lot_code} · sisa {l.qty_sisa}</option>)}
                </select>
              </div>
            );
          })}
          <Button variant="outline" size="sm" onClick={() => setForm(f => ({ ...f, items: [...f.items, barisKosong()] }))}>+ Baris bahan</Button>
          <Textarea rows={2} placeholder="Alasan pemindahan (min. 5 karakter)" value={form.alasan}
                    onChange={e => setForm({ ...form, alasan: e.target.value })} data-testid="pb-alasan" />
          <Button size="sm" disabled={busy} onClick={kirimPengajuan} data-testid="pb-ajukan">Ajukan</Button>
        </Card>
      )}

      <PalangDaftar daftar={daftar} placeholder="Cari nomor / alasan" testId="pb-palang"
                    ekspor={{ nama: "pindah_bahan", kolom: KOLOM_CSV, ambilHalaman }}>
        <select value={daftar.nilaiSaringan.status} onChange={(e) => daftar.setSaringan("status", e.target.value)}
                className="h-9 rounded border px-2 text-sm" aria-label="Saring status" data-testid="pb-saring-status">
          <option value="">Semua status</option>
          {Object.entries(LABEL_SARING).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </PalangDaftar>
      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
            <tr><ThUrut daftar={daftar} kunci="nomor" className="px-3 py-2">Nomor</ThUrut><th className="px-3 py-2 text-left">Asal → Tujuan</th>
              <ThUrut daftar={daftar} kunci="diajukan_pada" className="px-3 py-2">Diajukan</ThUrut>
              <ThUrut daftar={daftar} kunci="status" className="px-3 py-2">Status</ThUrut></tr>
          </thead>
          <tbody>
            {memuat && daftar.tampil.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground">Memuat…</td></tr>}
            {!memuat && daftar.tampil.length === 0 && <tr><td colSpan={4} className="px-3 py-6 text-center text-muted-foreground" data-testid="pb-kosong">
              {daftar.cari.trim() || daftar.saringanAktif.length ? "Tidak ada pemindahan yang cocok dengan pencarian atau saringan ini." : "Belum ada pemindahan bahan."}</td></tr>}
            {daftar.tampil.map(p => (
              <tr key={p.id} className="cursor-pointer border-t hover:bg-muted/30" onClick={() => bukaDetail(p.id)} data-testid={`pb-baris-${p.nomor}`}>
                <td className="px-3 py-2 font-mono">{p.nomor}</td>
                <td className="px-3 py-2">{p.store_asal_nama} → {p.store_tujuan_nama}</td>
                <td className="px-3 py-2">{formatDate(p.diajukan_pada)}</td>
                <td className="px-3 py-2"><StatusBadge tone={NADA[p.status]}>{LABEL[p.status] || p.status}</StatusBadge></td>
              </tr>
            ))}
          </tbody>
        </table>
        <Paginasi daftar={daftar} testId="pb-paginasi" />
      </Card>

      {detail && (
        <Card className="space-y-3 p-4 text-sm" data-testid="pb-detail">
          <div className="flex items-center justify-between">
            <div className="font-medium">{detail.nomor} · {detail.store_asal_nama} → {detail.store_tujuan_nama}</div>
            <StatusBadge tone={NADA[detail.status]}>{LABEL[detail.status] || detail.status}</StatusBadge>
          </div>
          <div className="text-muted-foreground">Alasan: {detail.alasan}{detail.alasan_tolak ? ` · Ditolak: ${detail.alasan_tolak}` : ""}{detail.alasan_batal ? ` · Batal: ${detail.alasan_batal}` : ""}</div>
          <table className="w-full text-sm">
            <thead className="text-xs text-muted-foreground"><tr><th className="text-left">Bahan</th><th className="text-right">Qty</th><th className="text-left">Lot</th><th className="text-right">Harga satuan (cabang asal)</th></tr></thead>
            <tbody>
              {(detail.items || []).map(it => (
                <tr key={it.id} className="border-t"><td>{it.material_code} · {it.material_name}</td><td className="text-right">{it.qty} {it.satuan}</td>
                  <td>{it.lot_code || "—"}</td><td className="text-right">{it.harga_satuan_idr == null ? "—" : formatIDR(it.harga_satuan_idr)}</td></tr>
              ))}
            </tbody>
          </table>
          {(detail.aksi || []).some(a => ["tolak", "batal"].includes(a)) && (
            <Textarea rows={2} placeholder="Alasan (wajib untuk tolak/batal, min. 5 karakter)" value={alasan} onChange={e => setAlasan(e.target.value)} />
          )}
          <div className="flex flex-wrap gap-2">
            {(detail.aksi || []).includes("setujui") && <Button size="sm" disabled={busy} onClick={() => aksi("putus", { setuju: true }, "Pemindahan disetujui.")} data-testid="pb-setujui">Setujui</Button>}
            {(detail.aksi || []).includes("tolak") && <Button size="sm" variant="outline" disabled={busy || alasan.trim().length < 5} onClick={() => aksi("putus", { setuju: false, alasan: alasan.trim() }, "Pemindahan ditolak.")} data-testid="pb-tolak">Tolak</Button>}
            {(detail.aksi || []).includes("kirim") && <Button size="sm" disabled={busy} onClick={() => aksi("kirim", null, "Bahan dikirim — stok keluar dari cabang asal.")} data-testid="pb-kirim">Kirim</Button>}
            {(detail.aksi || []).includes("terima") && <Button size="sm" disabled={busy} onClick={() => aksi("terima", null, "Bahan diterima — stok masuk cabang tujuan.")} data-testid="pb-terima">Terima</Button>}
            {(detail.aksi || []).includes("batal") && <Button size="sm" variant="destructive" disabled={busy || alasan.trim().length < 5} onClick={() => aksi("batal", { alasan: alasan.trim() }, "Pemindahan dibatalkan.")} data-testid="pb-batal">Batalkan</Button>}
            <Button size="sm" variant="ghost" onClick={() => setDetail(null)}>Tutup</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
